import { LoadingSpinner } from "components/loading-spinner/LoadingSpinner";
import { useEffect, useState } from "react";
import { sandboxApi } from "services/sandbox-api/api";
import { getOptionalFiles } from "services/sandbox-api/transforms";
import { useAppDispatch } from "store";
import { sandboxActionCompleted } from "store/features";
import { SvgDownload } from "@itwin/itwinui-icons-react";
import { Alert, Anchor, Button, Code, Dialog, Text } from "@itwin/itwinui-react";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import styles from "./ExportSandboxActionHandler.module.css";

interface ExportSandboxActionHandlerProps {
  sandboxId?: string;
  userId?: string;
}

const getZipName = (name: string) => {
  name = name.trim().toLowerCase().replaceAll(/\s/g, "-").replaceAll(/-+/g, "-");
  return `${name}.zip`;
}

export const ExportSandboxActionHandler = ({ sandboxId, userId }: ExportSandboxActionHandlerProps) => {
  const dispatch = useAppDispatch();

  const { data, error, isLoading } = sandboxApi.useCodeShareQuery(userId && sandboxId ? { userId, codeshareId: sandboxId } : skipToken);
  const [entryUndefined, setEntryUndefined] = useState(false);
  const [downloadBlob, setDownloadBlob] = useState<Blob | undefined>();
  const [isExporting, setIsExporting] = useState(true);
  const [exportError, setExportError] = useState<string>();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const onClose = () => {
    setIsModalOpen(false);
    setTimeout(() => dispatch(sandboxActionCompleted()), 200);
  };

  useEffect(() => {
    setTimeout(() => setIsModalOpen(true), 5);
  }, []);

  useEffect(() => {
    if (!data)
      return;

    setIsExporting(true);
    const files = [...getOptionalFiles(data.optionalFiles), ...data.files];
    const entry = data.files.find((file) => file.entry);

    if (!entry) {
      setEntryUndefined(true);
      setIsExporting(false);
      return;
    }

    import("@sandbox/export")
      .then((module) => {
        return module.default.download(data.name, files, entry.filePath, data.modules, data.itwinVersion);
      })
      .then((blob) => {
        setDownloadBlob(blob)
      })
      .catch((error) => {
        console.log(error);
        setExportError(JSON.stringify(error));
      })
      .finally(() => setIsExporting(false));
  }, []);

  return (
    <Dialog isOpen={isModalOpen} onClose={onClose} closeOnEsc closeOnExternalClick preventDocumentScroll trapFocus setFocus isDismissible>
      <Dialog.Backdrop />
      <Dialog.Main data-testid="atp-export-sandbox-dialog">
        <Dialog.TitleBar titleText="Export Sandbox" data-testid="atp-export-sandbox-title"/>
        <Dialog.Content className={styles.container}>
          <Text>
            Click on the link at the bottom to download this sandbox as a standalone project based on react template
          </Text>
          <Text>
            Once download is completed, unpack the archive to destination folder of your choice and set iTwin values in <Code>.env</Code> file.
            These values can be obtained from <Anchor href="https://developer.bentley.com">Bentley Developer Portal</Anchor>.
            You need to sign-in, navigate to <Anchor href="https://developer.bentley.com/my-imodels">My iModels</Anchor> page,
            create a new sample imodel, and then copy both <Code>ITWIN_ID</Code> and
            <Code>IMODEL_ID</Code> values and paste them to <Code>.env</Code> file of exported project
          </Text>
          <Code className={styles.code}>
            <code># ---- Test ids ----</code>
            <code>IMJS_ITWIN_ID=</code>
            <code>IMJS_IMODEL_ID=</code>
          </Code>
          <Text>
            It is required to create an application for authentication in <Anchor href="https://developer.bentley.com/my-apps/">My Apps</Anchor> page of Bentley Developer Portal.
            For more instructions please follow <Anchor href="https://www.itwinjs.org/learning/tutorials/develop-web-viewer/">Developing a Web Viewer</Anchor> guide and fill in
            these mandatory variables in <Code>.env</Code> file:
          </Text>
          <Code className={styles.code}>
            <code># ---- Authorization Client Settings ----</code>
            <code>IMJS_AUTH_CLIENT_CLIENT_ID=</code>
            <code>IMJS_AUTH_CLIENT_REDIRECT_URI=</code>
            <code>IMJS_AUTH_CLIENT_SCOPES=</code>
          </Code>
          <Text>
            Once configuration is done, install npm dependencies and start a localhost instance of the app as usual:
          </Text>
          <Code className={styles.code}>
            <code>npm install</code>
            <code>npm start</code>
          </Code>
          {entryUndefined && <Alert type="negative">Entry file is not set. Please set the main app file in the File Explorer to be entry file</Alert>}
          {error && <Alert type="negative">{error}</Alert>}
          {exportError && <Alert type="negative">{exportError}</Alert>}
        </Dialog.Content>
        <Dialog.ButtonBar className={styles.downloadContainer}>
          {(isLoading || isExporting) && <LoadingSpinner centered hPos="left" size="small" message="Processing..." />}
          {downloadBlob && data && !entryUndefined &&
            <Anchor href={URL.createObjectURL(downloadBlob)} download={getZipName(data.name)}>
              <div className={styles.downloadLink}>
                <SvgDownload />
                Sandbox Download is ready
              </div>
            </Anchor>
          }
          <Button onClick={onClose} data-testid="atp-export-close">Close</Button>
        </Dialog.ButtonBar>
      </Dialog.Main>
    </Dialog>
  )
};